import {
  useCallback, useEffect, useMemo, useState, useTransition,
} from 'react';
import {
  createLocationExportReportDemographicsQueries,
  getReportDemographicDataFromResults,
  getReportDemographicLabelFromResults,
  getReportTemplateDemographicIds,
} from '~/exportReport/exportReportData.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import {
  type DemographicsQueryBulkResults, type DemographicsResult, getDemographicsQueryBulk,
} from '~/store/demographics/demographics.repository';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { EXPORT_REPORT_LOCATION_RADIUS_TABLE_COLUMNS } from './useExportReportParams';

type UseExportReportDataParams = {
  isPreviewMode?: boolean;
  template: ReportTemplate | null;
};

export const useExportReportData = ({ isPreviewMode = false, template }: UseExportReportDataParams) => {
  const [data, setData] = useState<DemographicsQueryBulkResults | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, startTransition] = useTransition();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  // in the future we will have a way to specify the BG, but for now use h7-usa as a fallback
  const boundaryGroupId = useMemo(() => {
    // TODO: revert to h7-usa after it starts working with 5km radius
    return boundaryGroups.find(boundaryGroup => boundaryGroup.codename === 'ags-us-zip-codes')?.id ?? null;
  }, [boundaryGroups]);
  const exportReportState = useToolsStateExportReportSelector();

  const spreadsheetData = useLatLngSpreadsheetData();

  const center = useMemo(() => {
    if (!exportReportState.isActive || exportReportState.params.type !== ExportReportType.Location) {
      return null;
    }

    const rowData = spreadsheetData.getRow(exportReportState.params.data.markerId);

    return rowData ?? null;
  }, [exportReportState, spreadsheetData]);

  const getDemographicData = useCallback((columnId: string, demographicId: string): DemographicsResult | null => {
    if (isPreviewMode) {
      return null;
    }

    return getReportDemographicDataFromResults(data, columnId, demographicId);
  }, [data, isPreviewMode]);

  const getDemographicLabel = useCallback((demographicId: string): string | null => {
    return getReportDemographicLabelFromResults(data, demographicId);
  }, [data]);

  useEffect(() => {
    setIsError(false);

    if (!template || !center || !clientId || !mapId || isPreviewMode) {
      return;
    }

    const demographicIds = getReportTemplateDemographicIds(template);

    startTransition(async () => {
      try {
        const results = await getDemographicsQueryBulk(clientId,
          {
            queries: createLocationExportReportDemographicsQueries({
              boundaryGroupId,
              center,
              demographicIds,
              mapId,
              tableColumns: EXPORT_REPORT_LOCATION_RADIUS_TABLE_COLUMNS,
            }),
          });

        setData(results);
      }
      catch {
        setIsError(true);
      }
    });
  }, [center, template, clientId, mapId, isPreviewMode, boundaryGroupId]);

  useEffect(() => {
    if (!data) {
      return;
    }

    Object.assign(window, {
      isReportFullyLoaded: true,
    });
  }, [data]);

  return {
    isLoading,
    isError,
    getDemographicData,
    getDemographicLabel,
  };
};
